import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { addMonths, daysInMonth, fromIso, toIso, weekdayOf, type Building } from '@map/shared';
import { STRENGTH_GOLD_DAYS, strengthOn } from '@map/health';
import { fetchStrength, setStrengthBegan, setStrengthReset } from './api.js';

/**
 * Strength of self, as its own log: the count, a month of days, and the one
 * thing that can be done to a day — reset it. Every day since it began is held
 * unless said otherwise, so there is nothing to tick; from day 40 a day is
 * golden. Any past day can be reset (or the reset taken back), and the day it
 * began can be moved to match when it really did.
 */

const WEEKDAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function longDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

export function StrengthCard({ building }: { building: Building }) {
  const queryClient = useQueryClient();
  const history = useQuery({
    queryKey: ['strength', building.id],
    queryFn: () => fetchStrength(building.id),
  });
  const [picked, setPicked] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['strength', building.id] });
    await queryClient.invalidateQueries({ queryKey: ['planet-buildings'] });
    await queryClient.invalidateQueries({ queryKey: ['universe'] });
  };
  const reset = useMutation({
    mutationFn: ({ day, on }: { day: string; on: boolean }) => setStrengthReset(building.id, day, on),
    onSuccess: async () => {
      setConfirming(false);
      await refresh();
    },
  });
  const began = useMutation({
    mutationFn: (day: string) => setStrengthBegan(building.id, day),
    onSuccess: refresh,
  });

  useEffect(() => setConfirming(false), [picked]);

  if (!history.data) return <p className="text-sm text-text-muted">…</p>;
  const h = history.data;
  const day = picked ?? h.today;
  const state = { began: h.began, resets: h.resets };
  const countOn = strengthOn(state, day);
  const isReset = h.resets.includes(day);
  const golden = h.count >= STRENGTH_GOLD_DAYS;
  const toGold = Math.max(0, STRENGTH_GOLD_DAYS - h.count);
  const error = reset.error ?? began.error;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-end gap-4">
        <div className="flex flex-col">
          <span className="text-[0.6875rem] uppercase tracking-wide text-text-muted">Day</span>
          <span
            className="text-4xl leading-none tabular-nums"
            style={{ color: golden ? 'var(--strength-gold)' : h.count === 0 ? 'var(--strength-reset)' : 'var(--strength-held)' }}
          >
            {h.count}
          </span>
        </div>
        <div className="mb-0.5 flex flex-1 flex-col gap-1.5">
          <div className="h-1.5 overflow-hidden rounded-full bg-surface-2">
            <div
              className={golden ? 'golden-day h-full' : 'h-full'}
              style={{
                width: `${Math.min(100, (h.count / STRENGTH_GOLD_DAYS) * 100)}%`,
                background: golden
                  ? undefined
                  : 'linear-gradient(90deg, var(--strength-held), color-mix(in oklch, var(--strength-held), var(--strength-gold) 60%))',
              }}
            />
          </div>
          <span className="text-xs text-text-muted">
            {golden ? 'Golden' : `${toGold} ${toGold === 1 ? 'day' : 'days'} to gold`}
            <span className="mx-1.5 opacity-50">·</span>
            best {h.best}
          </span>
        </div>
      </div>

      <StrengthCalendar today={h.today} selected={day} onPick={setPicked} state={state} />

      <section className="flex flex-col gap-3 rounded-[var(--radius-md)] border border-border p-3">
        <header className="flex items-baseline gap-2">
          <h4 className="text-sm">{day === h.today ? 'Today' : longDay(day)}</h4>
          <span className="ml-auto text-sm tabular-nums text-text-muted">
            {countOn === null ? 'before it began' : isReset ? 'reset' : `day ${countOn}`}
          </span>
        </header>

        {countOn !== null &&
          (isReset ? (
            <button
              onClick={() => reset.mutate({ day, on: false })}
              disabled={reset.isPending}
              className="self-start rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm hover:bg-surface-1 disabled:opacity-50"
            >
              Take back this reset
            </button>
          ) : confirming ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => reset.mutate({ day, on: true })}
                disabled={reset.isPending}
                className="rounded-[var(--radius-sm)] px-3 py-1.5 text-sm disabled:opacity-50"
                style={{ background: 'var(--strength-reset)', color: 'white' }}
              >
                {reset.isPending ? 'Resetting…' : `Reset ${day === h.today ? 'today' : 'on this day'}`}
              </button>
              <button onClick={() => setConfirming(false)} className="px-2 py-1.5 text-sm text-text-muted hover:text-text">
                Keep it
              </button>
              <span className="w-full text-xs text-text-muted">The count goes back to 0 here and starts again the day after.</span>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              className="self-start rounded-[var(--radius-sm)] border border-border px-3 py-1.5 text-sm text-text-muted hover:border-[var(--strength-reset)] hover:text-text"
            >
              Reset…
            </button>
          ))}

        {day !== h.began && !isReset && (
          <button
            onClick={() => began.mutate(day)}
            disabled={began.isPending}
            className="self-start text-xs text-text-muted hover:text-text disabled:opacity-50"
          >
            {day < h.began ? 'It began on this day' : 'Count from this day instead'}
          </button>
        )}
        {error && <p className="text-xs text-text-muted">That did not save: {error.message}</p>}
      </section>

      <p className="text-xs text-text-muted">
        Began {longDay(h.began)}. Every day counts by itself; only a reset is ever marked.
      </p>
    </div>
  );
}

function StrengthCalendar({
  today,
  selected,
  onPick,
  state,
}: {
  today: string;
  selected: string;
  onPick: (day: string) => void;
  state: { began: string; resets: string[] };
}) {
  const [month, setMonth] = useState(() => {
    const d = fromIso(selected);
    return { year: d.year, month: d.month };
  });
  useEffect(() => {
    const d = fromIso(selected);
    setMonth({ year: d.year, month: d.month });
  }, [selected]);

  const resets = useMemo(() => new Set(state.resets), [state.resets]);
  const first = { ...month, day: 1 };
  const lead = (weekdayOf(first) + 6) % 7; // Monday first
  const length = daysInMonth(month.year, month.month);
  const title = new Date(Date.UTC(month.year, month.month - 1, 1)).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const atThisMonth = today.slice(0, 7) === toIso(first).slice(0, 7);
  const step = (n: number) => {
    const next = addMonths(first, n);
    setMonth({ year: next.year, month: next.month });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between text-sm">
        <button onClick={() => step(-1)} className="px-2 text-text-muted hover:text-text" aria-label="Previous month">
          ‹
        </button>
        <span>{title}</span>
        <button
          onClick={() => step(1)}
          disabled={atThisMonth}
          className="px-2 text-text-muted hover:text-text disabled:opacity-30"
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[0.6875rem]">
        {WEEKDAY_LETTERS.map((letter, i) => (
          <span key={i} className="text-text-muted">
            {letter}
          </span>
        ))}
        {Array.from({ length: lead }, (_, i) => (
          <span key={`lead-${i}`} />
        ))}
        {Array.from({ length }, (_, i) => {
          const day = toIso({ ...month, day: i + 1 });
          const future = day > today;
          const count = future ? null : strengthOn(state, day);
          const reset = resets.has(day) && count !== null;
          const gold = count !== null && count >= STRENGTH_GOLD_DAYS;
          const held = count !== null && !reset && !gold;
          return (
            <button
              key={day}
              disabled={future}
              onClick={() => onPick(day)}
              className={`flex aspect-square items-center justify-center rounded-[var(--radius-sm)] tabular-nums disabled:opacity-25 ${gold ? 'golden-day' : ''}`}
              style={{
                background: gold
                  ? undefined
                  : reset
                    ? 'color-mix(in oklch, var(--strength-reset) 70%, transparent)'
                    : held
                      ? 'color-mix(in oklch, var(--strength-held) 55%, transparent)'
                      : 'var(--surface-1)',
                outline:
                  day === selected
                    ? '2px solid var(--text)'
                    : day === today
                      ? '1px solid var(--border)'
                      : 'none',
                outlineOffset: '-1px',
              }}
              title={count === null ? day : reset ? `${day}: reset` : `${day}: day ${count}`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}
